import React from 'react';
import { C } from '../constants';
import { hubGet, hubPost } from '../services/hub';
import { tokenParaBackend } from '../services/adminAuth';

// La gestión de la tienda es administración: el servidor la gatea con `exigeAdmin`, y
// `hubPost`/`hubGet` no mandan la credencial del usuario. En los POST va en el cuerpo (que es
// donde `tokenSesion` la lee, igual que al aprobar una RETIE); en el GET, por cabecera.
const credencial = () => tokenParaBackend() || '';
const cabeceraAdmin = () => ({ headers: { Authorization: `Bearer ${credencial()}` } });

const TIPOS = [
  ['panel', 'Panel'],
  ['inverter', 'Inversor'],
  ['battery', 'Batería'],
  ['meter', 'Medidor'],
  ['modem', 'Gateway AMI'],
  ['structure', 'Estructura'],
  ['other', 'Accesorio / otro'],
];

const VACIO = {
  tipo: 'panel', sku: '', brand: '', model: '',
  wp: '', kw: '', kwh: '', unit_price_cop: '', qty_available: '0', lead_time_days: '3',
};

function FilaStock({ it, ss, onSaved }) {
  const [qty, setQty] = React.useState(String(it.qty_available ?? ''));
  const [precio, setPrecio] = React.useState(String(it.unit_price_cop ?? ''));
  const [sku, setSku] = React.useState(it.sku || '');
  const [brand, setBrand] = React.useState(it.brand || '');
  const [model, setModel] = React.useState(it.model || '');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    setQty(String(it.qty_available ?? ''));
    setPrecio(String(it.unit_price_cop ?? ''));
    setSku(it.sku || '');
    setBrand(it.brand || '');
    setModel(it.model || '');
  }, [it.id, it.qty_available, it.unit_price_cop, it.sku, it.brand, it.model]);

  async function guardar(extra = {}) {
    setBusy(true);
    try {
      const r = await hubPost('tienda/admin-stock', {
        token: credencial(),
        id: it.id,
        sku,
        brand,
        model,
        qty_available: qty === '' ? undefined : Number(qty),
        unit_price_cop: precio === '' ? undefined : Number(precio),
        ...extra,
      });
      if (r.item) onSaved(r.item);
    } catch (e) {
      onSaved(null, e.message);
    } finally { setBusy(false); }
  }

  const docs = [it.ficha_nombre && 'ficha', it.manual_nombre && 'manual', it.cert_nombre && 'cert'].filter(Boolean).join(' · ');

  return (
    <tr>
      <td style={ss.td}>{it.es_principal ? 'Alebas' : it.company}</td>
      <td style={ss.td}><input value={sku} onChange={(e) => setSku(e.target.value)} onBlur={() => guardar()} style={{ ...ss.inp, width: 92 }} disabled={busy} /></td>
      <td style={ss.td}>{it.equipment_type}</td>
      <td style={ss.td}>
        <input value={brand} onChange={(e) => setBrand(e.target.value)} onBlur={() => guardar()} style={{ ...ss.inp, width: 88, marginBottom: 4 }} disabled={busy} />
        <input value={model} onChange={(e) => setModel(e.target.value)} onBlur={() => guardar()} style={{ ...ss.inp, width: 110 }} disabled={busy} />
      </td>
      <td style={ss.td}>
        <input type="number" value={qty} onChange={(e) => setQty(e.target.value)}
          onBlur={() => guardar()} style={{ ...ss.inp, width: 64 }} disabled={busy} />
      </td>
      <td style={ss.td}>
        <input type="number" value={precio} onChange={(e) => setPrecio(e.target.value)}
          onBlur={() => guardar()} style={{ ...ss.inp, width: 104 }} disabled={busy} />
      </td>
      <td style={ss.td}>{docs || '—'}</td>
      <td style={ss.td}>{it.is_active ? 'vitrina' : 'oculto'}</td>
      <td style={ss.td}>
        <button type="button" style={ss.btn} disabled={busy}
          onClick={() => guardar({ is_active: !it.is_active })}>
          {it.is_active ? 'Ocultar' : 'Publicar'}
        </button>
      </td>
    </tr>
  );
}

export default function BackOfficeTienda({ ss }) {
  const [items, setItems] = React.useState([]);
  const [principal, setPrincipal] = React.useState(null);
  const [resumen, setResumen] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [msg, setMsg] = React.useState('');
  const [q, setQ] = React.useState('');
  const [tipo, setTipo] = React.useState('');
  const [vitrina, setVitrina] = React.useState('');
  const [alta, setAlta] = React.useState(false);
  const [form, setForm] = React.useState(VACIO);
  const [busyAlta, setBusyAlta] = React.useState(false);

  function cargar() {
    const qs = {};
    if (q.trim()) qs.q = q.trim();
    if (tipo) qs.tipo = tipo;
    if (vitrina) qs.vitrina = vitrina;
    return hubGet('tienda/admin-stock', qs, cabeceraAdmin()).then((r) => {
      setItems(r.items || []);
      setPrincipal(r.principal || null);
      setResumen(r.resumen || null);
    }).catch((e) => setErr(e.message || 'No se pudo cargar la gestión.'));
  }

  React.useEffect(() => { cargar(); }, [tipo, vitrina]);

  async function sembrar() {
    setErr(''); setMsg('');
    try {
      const r = await hubPost('tienda/sembrar-principal', { token: credencial() });
      setItems(r.items || []);
      setPrincipal(r.principal || principal);
      setMsg(`Línea Alebas en backend (${r.sembrados || 0} SKU nuevos).`);
      cargar();
    } catch (e) {
      setErr(e.message || 'No se pudo publicar la línea.');
    }
  }

  async function crear(e) {
    e.preventDefault();
    setBusyAlta(true); setErr(''); setMsg('');
    try {
      const r = await hubPost('tienda/admin-producto', {
        token: credencial(),
        ...form,
        unit_price_cop: form.unit_price_cop === '' ? 0 : Number(form.unit_price_cop),
        qty_available: form.qty_available === '' ? 0 : Number(form.qty_available),
      });
      if (r.item) {
        setItems((prev) => [r.item, ...prev]);
        setForm(VACIO);
        setAlta(false);
        setMsg(`Creado ${r.item.sku || r.item.model} en hub-api.`);
        cargar();
      }
    } catch (err) {
      setErr(err.message || 'No se pudo crear.');
    } finally { setBusyAlta(false); }
  }

  function onSaved(item, error) {
    if (error) { setErr(error); return; }
    setErr('');
    setItems((prev) => prev.map((x) => (String(x.id) === String(item.id) ? { ...x, ...item } : x)));
  }

  const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={ss.h2}>Gestión Solar Hub</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={ss.btn} onClick={() => setAlta((v) => !v)}>{alta ? 'Cerrar alta' : 'Nuevo producto'}</button>
          <button type="button" style={{ ...ss.btn, background: C.card, color: C.text, border: `1px solid ${C.border}` }} onClick={sembrar}>Sincronizar línea Alebas</button>
        </div>
      </div>

      {resumen && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(110px,1fr))', gap: 8, marginBottom: 12 }}>
          {[
            ['Total', resumen.total],
            ['En vitrina', resumen.vitrina],
            ['Ocultos', resumen.ocultos],
            ['Con stock', resumen.con_stock],
            ['Sin stock', resumen.sin_stock],
          ].map(([l, v]) => (
            <div key={l} style={{ ...ss.card, margin: 0, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{l}</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {principal && (
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
          Proveedor principal #{principal.id} · {principal.company} · {principal.email}. Todo se guarda en hub-api.
        </div>
      )}
      {err && <p style={{ color: C.red, fontSize: 12 }}>{err}</p>}
      {msg && <p style={{ color: C.green, fontSize: 12 }}>{msg}</p>}

      {alta && (
        <form onSubmit={crear} style={{ ...ss.card, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Alta en backend</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 8 }}>
            <label>Tipo
              <select value={form.tipo} onChange={(e) => u('tipo', e.target.value)} style={ss.inp}>
                {TIPOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label>SKU<input value={form.sku} onChange={(e) => u('sku', e.target.value)} style={ss.inp} /></label>
            <label>Marca<input required value={form.brand} onChange={(e) => u('brand', e.target.value)} style={ss.inp} /></label>
            <label>Modelo<input required value={form.model} onChange={(e) => u('model', e.target.value)} style={ss.inp} /></label>
            {form.tipo === 'panel' && <label>Wp<input type="number" value={form.wp} onChange={(e) => u('wp', e.target.value)} style={ss.inp} /></label>}
            {form.tipo === 'inverter' && <label>kW<input type="number" value={form.kw} onChange={(e) => u('kw', e.target.value)} style={ss.inp} /></label>}
            {form.tipo === 'battery' && <label>kWh<input type="number" value={form.kwh} onChange={(e) => u('kwh', e.target.value)} style={ss.inp} /></label>}
            <label>Precio COP<input type="number" value={form.unit_price_cop} onChange={(e) => u('unit_price_cop', e.target.value)} style={ss.inp} /></label>
            <label>Stock<input type="number" value={form.qty_available} onChange={(e) => u('qty_available', e.target.value)} style={ss.inp} /></label>
            <label>Lead (días)<input type="number" value={form.lead_time_days} onChange={(e) => u('lead_time_days', e.target.value)} style={ss.inp} /></label>
          </div>
          <button type="submit" style={{ ...ss.btn, marginTop: 10 }} disabled={busyAlta}>{busyAlta ? 'Guardando…' : 'Crear en hub-api'}</button>
        </form>
      )}

      <div style={ss.card}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && cargar()}
            placeholder="Buscar SKU, marca, modelo…" style={{ ...ss.inp, width: 220 }} />
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} style={{ ...ss.inp, width: 140 }}>
            <option value="">Todos los tipos</option>
            {TIPOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={vitrina} onChange={(e) => setVitrina(e.target.value)} style={{ ...ss.inp, width: 130 }}>
            <option value="">Vitrina: todas</option>
            <option value="si">Solo publicadas</option>
            <option value="no">Solo ocultas</option>
          </select>
          <button type="button" style={ss.btn} onClick={cargar}>Filtrar</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
            <thead>
              <tr>{['Proveedor', 'SKU', 'Tipo', 'Marca / modelo', 'Uds', 'Precio COP', 'Docs', 'Estado', ''].map((h) => (
                <th key={h} style={ss.th}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={9} style={ss.td}>Sin filas. Crea un producto o sincroniza la línea Alebas.</td></tr>
              )}
              {items.map((it) => (
                <FilaStock key={it.id} it={it} ss={ss} onSaved={onSaved} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
